import { useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import {
  useGetContentVideosQuery,
  useLazyGetContentVideoSyncQuery
} from '../../../../../services/endpoints/content/contentVideo';
import { Scopes } from '../../../../../services/endpoints/people/user';
import { trainingVideo } from '../../../../../types/trainingVideos.type';
import { contentVideosColumns } from '../../../../../utils/consts/columnDefs/contentVideos';
import { getFilterValue } from '../../../../../utils/listFilterSearchParams';

const defaultScope = 'all';

export const ContentVideosView = () => {
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl({
    scope: defaultScope,
    data: { page: 1, entries: 10 }
  });
  const [scopes, setScopes] = useState<Scopes>({});
  const { data, error, isLoading, isFetching } = useGetContentVideosQuery(filters);
  const [trainingVideo, setTrainingVideo] = useState<trainingVideo[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const navigate: NavigateFunction = useNavigate();
  const [triggerSync, { data: syncData, error: syncError, isLoading: syncLoading }] =
    useLazyGetContentVideoSyncQuery();
  const [syncTrigger, setSyncTrigger] = useState(false); // Local state to trigger the sync query

  const filterFormFields: FilterField[] = [
    {
      name: 'name',
      label: 'Name',
      value: getFilterValue(filters.data?.filterBy, 'name')
    },
    {
      name: 'id',
      label: 'Id',
      value: getFilterValue(filters.data?.filterBy, 'id')
    },
    {
      name: 'vimeo_id',
      label: 'Vimeo id',
      value: getFilterValue(filters.data?.filterBy, 'vimeo_id')
    }
  ];

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
    if (data && data.results) {
      setTrainingVideo(data.results);
    }
  }, [data]);

  useEffect(() => {
    if (!syncError && !syncLoading && syncData && syncData.results) {
      navigate('/admin/content/videos');
      //navigate to current page
    } else if (syncError) {
      addToast('Error while sync data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncData, syncError, syncLoading]);

  // Button handler to trigger sync
  const handleSyncVideos = () => {
    triggerSync();
  };

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
    <div className="py-2">
      <SFCTable
        name={`${scopes[filters?.scope!] ?? ''} Videos`}
        columns={contentVideosColumns}
        data={trainingVideo}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={setFilters}
        newItemEl={
          <SimpleButton
            variant="sfc-blueberry"
            height={40}
            truncate={false}
            onClick={handleSyncVideos}
          >
            Sync with vimeo
          </SimpleButton>
        }
      />
    </div>
  );
};
